/* assets/zipify-product-configurator.js
   Zipify Pages version of the configurator. It does not own an Add to cart button:
   it keeps the Zipify Buy Box form (and TimesAct) pointed at the chosen variant and
   adds the separate Extra Paint Set product when that form is submitted. */
if (!customElements.get('zipify-product-configurator')) {
  customElements.define(
    'zipify-product-configurator',
    class ZipifyProductConfigurator extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
        } else {
          this.init();
        }
      }

      init() {
        if (this.initialized) return;
        this.initialized = true;
        this.variantInputs = Array.from(this.querySelectorAll('.product-configurator__radio'));
        this.addonInput = this.querySelector('.product-configurator__addon-checkbox');
        this.addonContainer = this.querySelector('[data-configurator-addon]');
        this.addonPriceElement = this.querySelector('[data-configurator-addon-price]');
        this.addonSoldOutElement = this.querySelector('[data-configurator-addon-sold-out]');
        this.errorElement = this.querySelector('[data-configurator-error]');
        this.totalElement = this.querySelector('[data-configurator-total]');

        this.variantInputs.forEach((input) => input.addEventListener('change', () => this.onVariantChange(input)));
        this.addonInput?.addEventListener('change', () => this.onAddonChange());
        this.querySelectorAll('[data-configurator-preview]').forEach((button) => {
          button.addEventListener('click', (event) => this.openPreview(event));
        });
        this.querySelectorAll('[data-configurator-close]').forEach((button) => {
          button.addEventListener('click', () => button.closest('dialog')?.close());
        });
        this.querySelectorAll('dialog').forEach((dialog) => {
          dialog.addEventListener('click', (event) => {
            if (event.target === dialog) dialog.close();
          });
        });
        this.querySelectorAll('[data-configurator-gallery]').forEach((gallery) => this.initializeGallery(gallery));

        // Quantity lives in the Zipify Buy Box, so listen on the document.
        document.addEventListener('change', (e) => {
          if (e.target?.name === 'quantity') this.updateTotal();
        });
        document.addEventListener('click', (e) => {
          if (e.target.closest?.('[name="plus"], [name="minus"], .quantity__button')) setTimeout(() => this.updateTotal(), 60);
        });

        this.installCartHooks();

        const selected = this.selectedVariant;
        if (selected) {
          this.syncAddonForVariant(selected);
          this.syncSelectedVariant();
        }
        this.updateTotal();

        // The Buy Box can render after this block, so sync again once the page has settled.
        window.addEventListener('load', () => this.syncSelectedVariant());
        setTimeout(() => this.syncSelectedVariant(), 1200);
      }

      get selectedVariant() {
        return this.variantInputs.find((input) => input.checked && !input.disabled);
      }

      get quantityInput() {
        return (
          document.querySelector('form[action*="/cart/add"] input[name="quantity"]') ||
          document.querySelector('input[name="quantity"]')
        );
      }

      get quantity() {
        const parsed = Number.parseInt(this.quantityInput?.value || '1', 10);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
      }

      get addonMode() {
        return this.dataset.addonMode || 'none';
      }

      get activeVariantId() {
        const selected = this.selectedVariant;
        if (!selected) return '';
        if (this.addonMode === 'paired' && this.addonInput?.checked && selected.dataset.addonVariantId) {
          return selected.dataset.addonVariantId;
        }
        return selected.value;
      }

      get addonUnitPrice() {
        if (this.addonMode === 'paired') return Number.parseInt(this.selectedVariant?.dataset.addonPrice || '0', 10);
        return Number.parseInt(this.dataset.addonPrice || '0', 10);
      }

      formatMoney(cents) {
        const currency = this.dataset.currencyCode || 'USD';
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(cents / 100);
        } catch (_e) {
          return `${currency} ${(cents / 100).toFixed(2)}`;
        }
      }

      onVariantChange(input) {
        if (input.disabled) return;
        this.applyQuantityRules(input);
        this.syncAddonForVariant(input);
        this.updateTotal();
        this.syncSelectedVariant();
      }

      onAddonChange() {
        if (this.addonInput?.disabled) return;
        this.updateTotal();
        if (this.addonMode === 'paired') this.syncSelectedVariant();
      }

      // Point every Shopify product form on the page (the Zipify Buy Box, TimesAct) at the chosen variant.
      syncSelectedVariant() {
        const variantId = this.activeVariantId;
        if (!variantId) return;
        this.dataset.selectedVariantId = variantId;

        document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
          form.querySelectorAll('input[name="id"], select[name="id"]').forEach((field) => {
            if (field.tagName === 'SELECT' && !Array.from(field.options).some((o) => o.value === String(variantId))) return;
            field.value = variantId;
            field.disabled = false;
            field.dispatchEvent(new Event('change', { bubbles: true }));
          });
        });
        document.dispatchEvent(new CustomEvent('configurator:variant-change', { detail: { variantId } }));

        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        window.history.replaceState({}, '', url.toString());
      }

      // Adds the separate Extra Paint Set whether the Buy Box submits by AJAX or by a normal form post.
      installCartHooks() {
        if (window.__zpcHooked) return;
        window.__zpcHooked = true;

        const originalFetch = window.fetch.bind(window);
        window.fetch = async (input, init) => {
          const url = typeof input === 'string' ? input : input?.url || '';
          if (/\/cart\/add(\.js)?(\?|$)/.test(url) && !window.__zpcAddonDone) {
            const configurator = document.querySelector('zipify-product-configurator');
            if (configurator?.shouldAddAddon(init?.body)) {
              try {
                await configurator.addAddon(originalFetch);
              } catch (error) {
                configurator.showError(error.message);
                throw error;
              }
            }
          }
          window.__zpcAddonDone = false;
          return originalFetch(input, init);
        };

        // Normal (non-AJAX) form posts: add the add-on first, then submit again.
        document.addEventListener(
          'submit',
          async (event) => {
            const form = event.target;
            const configurator = document.querySelector('zipify-product-configurator');
            if (!configurator || !form?.action || !/\/cart\/add/.test(form.action) || form.__zpcResubmit) return;
            if (form.classList.contains('installment')) return;
            const idField = form.querySelector('[name="id"]');
            if (!configurator.shouldAddAddon({ id: idField?.value }, true)) return;

            event.preventDefault();
            event.stopImmediatePropagation();
            try {
              await configurator.addAddon(originalFetch);
              window.__zpcAddonDone = true; // stops the fetch hook adding it a second time
              setTimeout(() => (window.__zpcAddonDone = false), 3000);
              form.__zpcResubmit = true;
              form.requestSubmit(event.submitter || undefined);
            } catch (error) {
              configurator.showError(error.message);
            } finally {
              form.__zpcResubmit = false;
            }
          },
          true
        );
      }

      shouldAddAddon(body, isObject = false) {
        if (this.addonMode !== 'separate') return false;
        if (!this.addonInput?.checked || !this.dataset.addonVariantId) return false;

        let requestedId = '';
        if (isObject) {
          requestedId = String(body?.id || '');
        } else if (body instanceof FormData) {
          requestedId = String(body.get('id') || '');
        } else if (typeof body === 'string') {
          try {
            const parsed = JSON.parse(body);
            requestedId = String(parsed.id ?? parsed.items?.[0]?.id ?? '');
          } catch (_e) {
            requestedId = String(new URLSearchParams(body).get('id') || '');
          }
        }
        return requestedId === String(this.activeVariantId);
      }

      async addAddon(fetchFn) {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetchFn(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [
              {
                id: Number.parseInt(this.dataset.addonVariantId, 10),
                quantity: this.dataset.addonQuantity === 'one' ? 1 : this.quantity,
              },
            ],
          }),
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          throw new Error(result.description || 'Unable to add the Extra Paint Set.');
        }
        this.showError();
      }

      syncAddonForVariant(input) {
        if (this.addonMode !== 'paired' || !this.addonInput) return;

        const addonVariantId = input.dataset.addonVariantId || '';
        const addonPrice = Number.parseInt(input.dataset.addonPrice || '0', 10);
        const addonAvailable = Boolean(addonVariantId) && input.dataset.addonAvailable === 'true';

        this.dataset.addonVariantId = addonVariantId;
        this.dataset.addonPrice = String(addonPrice);
        this.addonInput.disabled = !addonAvailable;
        if (!addonAvailable) this.addonInput.checked = false;
        this.addonContainer?.classList.toggle('is-unavailable', !addonAvailable);
        if (this.addonSoldOutElement) {
          this.addonSoldOutElement.hidden = addonAvailable;
          this.addonSoldOutElement.setAttribute('aria-hidden', String(addonAvailable));
          this.addonSoldOutElement.style.setProperty('display', addonAvailable ? 'none' : '', 'important');
        }
        if (this.addonPriceElement) this.addonPriceElement.textContent = `(+${this.formatMoney(addonPrice)})`;
      }

      applyQuantityRules(input) {
        const qty = this.quantityInput;
        if (!qty) return;
        const min = Number.parseInt(input.dataset.quantityMin || '1', 10);
        const max = Number.parseInt(input.dataset.quantityMax || '', 10);
        const step = Number.parseInt(input.dataset.quantityStep || '1', 10);
        qty.min = String(min);
        qty.step = String(step);
        if (Number.isFinite(max)) qty.max = String(max);
        else qty.removeAttribute('max');
        if (Number.parseInt(qty.value, 10) < min) qty.value = String(min);
        if (Number.isFinite(max) && Number.parseInt(qty.value, 10) > max) qty.value = String(max);
      }

      updateTotal() {
        if (!this.totalElement) return;
        const selected = this.selectedVariant;
        if (!selected) {
          this.totalElement.textContent = '';
          return;
        }
        const quantity = this.quantity;
        let total = Number.parseInt(selected.dataset.variantPrice || '0', 10) * quantity;
        if (this.addonInput?.checked) {
          const addonQuantity = this.addonMode === 'paired' || this.dataset.addonQuantity !== 'one' ? quantity : 1;
          total += this.addonUnitPrice * addonQuantity;
        }
        this.totalElement.textContent = this.formatMoney(total);
      }

      openPreview(event) {
        event.preventDefault();
        event.stopPropagation();
        const dialog = document.getElementById(event.currentTarget.dataset.configuratorPreview);
        if (!dialog) return;
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
      }

      initializeGallery(gallery) {
        const track = gallery.querySelector('[data-configurator-gallery-track]');
        const previousButton = gallery.querySelector('[data-configurator-gallery-previous]');
        const nextButton = gallery.querySelector('[data-configurator-gallery-next]');
        const currentSlide = gallery.querySelector('[data-configurator-gallery-current]');
        if (!track || !previousButton || !nextButton || !currentSlide) return;

        const updateControls = () => {
          const slideWidth = track.clientWidth;
          const index = slideWidth ? Math.round(track.scrollLeft / slideWidth) : 0;
          const lastIndex = track.children.length - 1;
          currentSlide.textContent = String(index + 1);
          previousButton.disabled = index <= 0;
          nextButton.disabled = index >= lastIndex;
        };

        previousButton.addEventListener('click', () => {
          track.scrollBy({ left: -track.clientWidth, behavior: 'smooth' });
        });
        nextButton.addEventListener('click', () => {
          track.scrollBy({ left: track.clientWidth, behavior: 'smooth' });
        });
        track.addEventListener('scroll', updateControls, { passive: true });
        updateControls();
      }

      showError(message = '') {
        if (!this.errorElement) return;
        this.errorElement.textContent = message;
        this.errorElement.hidden = !message;
      }
    }
  );
}